import React from 'react';
import { Box, Flex, TextV2 } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { HONEYMOON_TAG } from '@shared/constants/accommodations';
import { useAccommodationsListingMobileLayout } from '@apps/stays/AccommodationsConsole/hooks/useAccommodationsListingMobileLayout';
import { usePrioritizeTagsQueryParam } from '@apps/stays/AccommodationsConsole/hooks/useAccommodationsConsoleSearchParamsState/usePrioritizeTagsQueryParam';
import type { Maybe } from '@graphql/generated-base';

type PrioritizedTagPillsProps = Readonly<{
  /** `listing.tags` — every curated tag on this property, as travel_service returns it. */
  tags?: Maybe<ReadonlyArray<string>>;
  /** The tags the page asked for. A caller that already read them passes them in so the URL is
   *  not parsed a second time per card; otherwise this component reads them itself. */
  requestedTags?: ReadonlyArray<string>;
  /** Shares a row with another badge; the caller does the aligning. */
  inline?: boolean;
}>;

/** The one list of tags this component can label. Adding one here fails the build until it gets a label. */
const LABELLED_TAGS = [HONEYMOON_TAG, 'beach', 'wellness', 'nature', 'luxury', 'adventure'] as const;
type LabelledTag = (typeof LABELLED_TAGS)[number];

const LABELLED_TAG_SET: ReadonlySet<string> = new Set(LABELLED_TAGS);
const isLabelledTag = (tag: string): tag is LabelledTag => LABELLED_TAG_SET.has(tag);

/**
 * The tags a card names: those the guest asked for (`?prioritizeTag=`) that this component can
 * label. `listing.tags` carries EVERY tag on the place, and most honeymoon hotels also carry two or
 * three trip types, so labelling the whole set would turn one "Honeymoon" pill into a row of five
 * on a plain honeymoon search. Naming only the requested tags keeps the pill's meaning — "why this
 * stay is here" — and the row short.
 */
const requestedLabelledTags = (tags: Maybe<ReadonlyArray<string>> | undefined, requestedTags: ReadonlyArray<string>): LabelledTag[] =>
  (tags ?? []).filter((tag): tag is LabelledTag => isLabelledTag(tag) && requestedTags.includes(tag));

/**
 * Whether any pill would render. `tags.length` is the wrong question — an unlabelled or
 * unrequested tag draws nothing. Takes the requested tags so a caller outside the component can
 * agree with it exactly.
 */
export const hasLabelledPrioritizedTag = (tags: Maybe<ReadonlyArray<string>> | undefined, requestedTags: ReadonlyArray<string>): boolean =>
  requestedLabelledTags(tags, requestedTags).length > 0;

/**
 * Names why a stay is near the top: the curated tag it matched.
 *
 * Only ever appears when the guest asked for the lift. travel_service populates `tags` on a
 * property *only* when the request carried `prioritizeTags`, so an unchecked filter means no tags
 * come back and no pill renders — the badge and the ordering cannot disagree.
 *
 * Sits in the same slot as CouplesPick, above the name, rather than over the photo: the photo's
 * bottom-left corner already belongs to SponsoredTag, and a sponsored honeymoon stay would collide
 * there.
 */
export const PrioritizedTagPills = ({ tags, requestedTags, inline }: PrioritizedTagPillsProps) => {
  const { tq } = useTranslation('accommodationsConsole');
  const { isMobileOrTablet } = useAccommodationsListingMobileLayout();

  /**
   * Unlabelled tags render nothing rather than falling back to the raw slug — a badge reading
   * `spa_luxury` looks like a bug on a guest-facing card. The filter chooser takes the opposite
   * view and does show the raw tag, because there an unlabelled row is still a working control
   * (see PrioritizeTagsFacet). The vocabulary itself is `SUPPORTED_TAGS` in travel_inventory_service.
   */
  // Keyed by LabelledTag, so this and `hasLabelledPrioritizedTag` cannot drift: a tag added to
  // LABELLED_TAGS with no label here is a type error.
  const labelByTag: Readonly<Record<LabelledTag, string>> = {
    [HONEYMOON_TAG]: tq`Honeymoon`,
    beach: tq`Beach`,
    wellness: tq`Wellness`,
    nature: tq`Nature`,
    luxury: tq`Luxury`,
    adventure: tq`Adventure`,
  };
  // Same hook the search request and the sort read, so the pills cannot name a tag the page did
  // not ask for. A caller's copy wins so a list does not parse the URL once per card.
  const requestedTagsFromUrl = usePrioritizeTagsQueryParam();
  const effectiveRequestedTags = requestedTags ?? requestedTagsFromUrl;
  const labelled = requestedLabelledTags(tags, effectiveRequestedTags).map(tag => ({ tag, label: labelByTag[tag] }));

  if (!labelled.length) {
    return null;
  }

  return (
    <Flex
      gap={2}
      flexWrap="wrap"
      alignItems="center"
      {...(inline ? {} : { width: '100%', justifyContent: { _: 'flex-start', md: 'flex-end' } })}
    >
      {labelled.map(({ tag, label }) => (
        <Box
          key={tag}
          paddingX={3}
          paddingY={1}
          borderRadius={2}
          color="primary8"
          backgroundColor="primary2"
        >
          <TextV2 typographyVariant={isMobileOrTablet ? 'label1' : 'label2'}>{label}</TextV2>
        </Box>
      ))}
    </Flex>
  );
};
